import validationLimits from 'pubky-app-specs/validationLimits.json';

/**
 * Regex pattern for parsing H:M:S timestamp format.
 * Matches formats: "1h2m3s", "5m30s", "45s", "30" (plain seconds)
 *
 * Pattern breakdown:
 * - `(?:(\d+)h)?` - Optional hours with 'h' suffix
 * - `(?:(\d+)m)?` - Optional minutes with 'm' suffix
 * - `(?:(\d+)s?)?` - Optional seconds with optional 's' suffix
 *
 * @example
 * "1h2m3s".match(HMS_TIMESTAMP_REGEX) // ["1h2m3s", "1", "2", "3"]
 * "30s".match(HMS_TIMESTAMP_REGEX)    // ["30s", undefined, undefined, "30"]
 * "30".match(HMS_TIMESTAMP_REGEX)     // ["30", undefined, undefined, "30"]
 * "5m".match(HMS_TIMESTAMP_REGEX)     // ["5m", undefined, "5", undefined]
 */
export const HMS_TIMESTAMP_REGEX = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/;

/**
 * Banned characters for tags per pubky-app-specs.
 * Colons, commas, spaces, tabs, and newlines are not allowed in tags.
 */
const escapeTagCharForRegex = (char: string) => {
  switch (char) {
    case '\t':
      return '\\t';
    case '\n':
      return '\\n';
    case '\r':
      return '\\r';
    default:
      return char.replace(/[\\\-\]\^]/g, '\\$&');
  }
};

const TAG_INVALID_CHARS_FOR_REGEX = validationLimits.tagInvalidChars.map(escapeTagCharForRegex).join('');

export const TAG_BANNED_CHARS = new RegExp(`[${TAG_INVALID_CHARS_FOR_REGEX}]`, 'g');

/**
 * Matches standalone hashtags in plain paragraph text (capture groups: leading whitespace, full hashtag).
 * Body allows any character except pubky-app-specs tagInvalidChars; first body char must be alphanumeric.
 * Use with isValidTagLabel before converting to a link.
 */
export const HASHTAG_IN_TEXT_REGEX = new RegExp(`(^|\\s)(#[a-zA-Z0-9][^${TAG_INVALID_CHARS_FOR_REGEX}]*)`, 'g');

/**
 * Name shown for a deleted user. Also the legacy Nexus tombstone sentinel: builds without the
 * `deleted` flag overwrite the user's `name` with it.
 */
export const DELETED_USER_NAME = '[DELETED]';
